import React, { useState, useMemo, useEffect } from "react";
import { SideBarContentHeader } from "@/common/ui/SideBar";
import EmptyList from "@/components/common/EmptyList";
import { RoundLoader } from "@/common/ui/Loader";
import Icon from "@/common/ui/Image";
import { Button } from "@/components/ui/button";
import { ModalConfig, withModal } from "@/provider/withModalProvider";
import ImportSummary from "./modal/ImportSummary";
import { useImportHistory } from "@/services/employee.services";
import SimpleTableView from "@/components/common/table/SimpleTableView";
import { ColumnDef } from "@tanstack/react-table";
import Pagination from "@/common/Table/ItemsPerPage";
import RenderColumn from "@/components/employee/RenderColumn";
import { useSelector } from "react-redux";
import { pageEventSelector } from "@/store/features/dashboardSlice";
import { useSearchParams } from "react-router-dom";

interface ImportHistoryRecord {
  id: string;
  ext_id: string;
  emp_id: number;
  org_id: number;
  status: {
    label: string;
    color: string;
  };
  file_info: {
    csv_url_key: string;
    field_mapping: Record<string, string>;
  };
  error_info: any;
  count: number;
  imported_count: number;
  skipped_count: number;
  updated_count: number;
  added_dt: string;
  added_ts: string;
  updated_dt: string;
  updated_ts: string;
  is_active: boolean;
  is_deleted: boolean;
  status_label: string;
  isExpired?: boolean;
  importedBy?: {
    full_name: string;
    profileText: string;
  };
}

interface PaginationState {
  pageNumber: number;
  pageSize: number;
}
const modalConfig: ModalConfig = {
  importSummary: {
    component: ImportSummary,
    title: "Import summary",
    type: "side",
    showCloseWarning: true,
    modalClassName: " md:!max-w-[600px]  ",
  },
};
interface ImportHistoryProps {
  openModal: (modalKey: string, props?: any) => void;
}
const ImportHistoryComponent: React.FC<ImportHistoryProps> = ({ openModal }) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageNumber: 1,
    pageSize: 20,
  });
  const pageEvent = useSelector(pageEventSelector);
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const {
    data: apiResponse,
    isLoading,
    isFetching,
    refetch,
  } = useImportHistory({
    page: pagination.pageNumber,
    per_page: pagination.pageSize,
  });

  const importData = useMemo(() => apiResponse?.data || [], [apiResponse?.data]);
  const totalRecords = apiResponse?.pagination?.total || 0;

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, pageNumber: page }));
  };

  const handlePerPageChange = (size: any) => {
    setPagination({ pageNumber: 1, pageSize: size.value });
  };

  const totalPages = apiResponse?.pagination?.total_pages || Math.ceil(totalRecords / pagination.pageSize);

  // Transform data to ensure id is string for TableView compatibility
  const transformedData = useMemo(() => {
    return importData.map((item: any) => ({
      ...item,
      id: item.id?.toString() || item.ext_id,
    }));
  }, [importData]);

  const columns = useMemo<ColumnDef<ImportHistoryRecord>[]>(
    () => [
      {
        id: "ext_id",
        header: "Import ID",
        accessorKey: "ext_id",
        cell: ({ row }) => {
          const { imported_count, updated_count, skipped_count } = row.original;
          let defaultTab: "Added" | "Updated" | "Skipped" = "Added";
          if (imported_count > 0) defaultTab = "Added";
          else if (updated_count > 0) defaultTab = "Updated";
          else if (skipped_count > 0) defaultTab = "Skipped";
          return (
            <div
              className={`text-sm font-medium ${
                row.original.isExpired ? "text-gray-400 cursor-not-allowed" : "text-text-primary hover:cursor-pointer hover:underline"
              }`}
              onClick={row.original.isExpired ? undefined : () => openModal("importSummary", { importId: row.original.ext_id, defaultTab })}
            >
              {row.original.ext_id}
            </div>
          );
        },
      },
      {
        id: "imported_count",
        header: "Added",
        accessorKey: "imported_count",
        size: 90,
        cell: ({ row }) => <div className="text-sm text-text-default">{row.original.imported_count}</div>,
      },
      {
        id: "updated_count",
        header: "Updated",
        accessorKey: "updated_count",
        size: 90,
        cell: ({ row }) => <div className="text-sm text-text-default">{row.original.updated_count}</div>,
      },
      {
        id: "skipped_count",
        header: "Skipped",
        accessorKey: "skipped_count",
        size: 90,
        cell: ({ row }) => <div className="text-sm text-text-default">{row.original.skipped_count}</div>,
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => (
          <span
            className="px-2 py-[3px] rounded-full text-xs leading-normal font-medium text-white"
            style={{
              backgroundColor: row.original.status?.color,
              color: "white",
            }}
          >
            {row.original.status?.label || "Unknown"}
          </span>
        ),
      },
      {
        id: "importedBy",
        header: "Imported by",
        accessorKey: "importedBy",
        cell: ({ row, column }: any) => <RenderColumn type={"profile"} row={row} column={column} />,
      },
      {
        id: "added_ts",
        header: "Imported on",
        accessorKey: "added_ts",
        cell: ({ row }) => (
          <div className="text-sm text-text-default">
            {new Date(row.original.added_ts).toLocaleString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })}
          </div>
        ),
      },
      {
        id: "action",
        header: "Action",
        cell: ({ row }) => (
          <Button
            variant="ghost"
            onClick={row.original.isExpired ? undefined : () => openModal("importSummary", { importId: row.original.ext_id })}
            disabled={row.original.isExpired}
            className={`px-3 py-1 h-auto text-xs ${
              row.original.isExpired ? "text-gray-400 cursor-not-allowed" : "text-text-primary hover:bg-blue-50 hover:text-blue-700"
            }`}
          >
            View
          </Button>
        ),
      },
    ],
    [openModal]
  );
  useEffect(() => {
    if (pageEvent?.keys.includes("importHistory") && pageEvent?.type === "refresh") {
      refetch();
    }
  }, [pageEvent]);

  return (
    <div className="flex flex-col flex-1 h-full w-full bg-white">
      <SideBarContentHeader className="!pb-3.5 !pt-0">
        <div className="flex items-center">
          <h2 className="font-semibold !leading-normal text-sm md:text-base text-text-default">
            Import History
            {totalRecords !== undefined && <span className="px-0.5 pt-0.5 text-xs leading-normal">{` (${totalRecords})`}</span>}
          </h2>

          <Button variant="ghost" size="sm" onClick={() => refetch()} className="p-2 hover:bg-gray-100 rounded-full">
            <Icon src="/icons/refresh.svg" alt="refresh icon" className={`size-5`} />
          </Button>
        </div>
      </SideBarContentHeader>

      <div className="flex flex-col h-[calc(100dvh-114px)] overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <RoundLoader />
          </div>
        ) : transformedData.length === 0 ? (
          <EmptyList text="No import history found" icon="/icons/empty-list-file.svg" className="h-full gap-y-4" />
        ) : (
          <div className="flex flex-col h-full min-h-0">
            <div className="flex-1 min-h-0 overflow-hidden">
              <SimpleTableView
                data={transformedData}
                columnsConfig={columns}
                hidePagination={true}
                className="h-full"
                columnClassName="max-w-[200px] w-[200px] min-w-[200px]"
                isLoading={isFetching}
                tableClassName="border-b-0 border-t-0"
                highlightId={id || ""}
              />
            </div>

            {/* Fixed Pagination at Bottom */}
            {totalRecords > 0 && (
              <div className="flex-shrink-0 border-t border-stroke-light bg-white">
                <Pagination
                  currentPage={pagination.pageNumber}
                  totalPages={totalPages}
                  perPage={{ label: `Records per page ${pagination.pageSize}`, value: pagination.pageSize }}
                  perPageOptions={[10, 20, 50, 100]}
                  onPageChange={handlePageChange}
                  onPerPageChange={handlePerPageChange}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ImportHistory = withModal(ImportHistoryComponent, modalConfig);
ImportHistory.displayName = "ImportHistory";

export default ImportHistory;
